import { AxiosError } from 'axios';
import SelectSingleRestaurantModal from 'components/SelectSingleRestaurantModal';
import AlertModal from 'components/common/AlertModal';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import deliveryService from 'features/Delivery/deliveryService';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Modal, ModalProps, Row } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { TARGET_SCHEMA_DEFAULT, UNIT_TEST_REFLECTION } from '../../constants';
import { TargetSchema, TestReflectionDelivery } from '../../models/delivery';
import CustomInputDatePicker from '../CustomInputDatePicker';

const TestReflectionModal = forwardRef<ModalProps, { onCreateSuccess: () => void }>((props, ref) => {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // state
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showModal, setShowModal] = useState(false);
  const [dataDate, setDataDate] = useState<Date | null>(new Date());
  const [showSelectDepartmentModal, setShowSelectDepartmentModal] = useState(false);
  const [restaurantId, setRestaurantId] = useState<number | null>(null);
  const [targetSchemaList, setTargetSchemaList] = useState<TargetSchema[]>([TARGET_SCHEMA_DEFAULT]);
  const [selectedTargetSchema, setSelectedTargetSchema] = useState(TARGET_SCHEMA_DEFAULT.target_schema);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setSelectedDate(date);
    }
  };

  const fetchTargetSchemaList = async () => {
    try {
      const response = await deliveryService.getTargetSchemaList();
      setTargetSchemaList((prevList) => [...prevList, ...response.data]);
    } catch (e: AxiosError | any) {
      console.error('Error fetching target schema list:', e.message);
      const data: any = e.response?.data;
      showGlobalModal(() => <AlertModal title={t('App.info')} message={e.message || data?.message} />);
    }
  };

  useEffect(() => {
    setShowSelectDepartmentModal(true);
  }, [showSelectDepartmentModal]);

  useImperativeHandle(ref, () => ({
    closeModal() {
      setShowModal(false);
    },
    openModal() {
      setShowModal(true);
      setDataDate(new Date());
      setShowSelectDepartmentModal(false); // reset state of show SelectDepartment component
      setRestaurantId(null); // clear state targetRestaurantId
      fetchTargetSchemaList();
    },
  }));

  function handleReturnSelectedRestaurant(node: any) {
    if (node !== null && 'restaurant_id' in node && node.classification === 'T') {
      setRestaurantId(node.restaurant_id);
    }
  }

  const handleSelectedTargetSchema = (event: any) => {
    const selectedOptionValue = event.target.value;
    setSelectedTargetSchema(selectedOptionValue);
  };

  const resetTargetSchema = () => {
    setTargetSchemaList([TARGET_SCHEMA_DEFAULT]);
    setSelectedTargetSchema(TARGET_SCHEMA_DEFAULT.target_schema);
  };

  const handleCreateDeliverySettings = async () => {
    let data: TestReflectionDelivery = {
      restaurant_id: restaurantId,
      target_schema: selectedTargetSchema,
      test_date: selectedDate.toISOString().slice(0, 10),
    };
    await deliveryService
      .createTestReflectionDelivery(data)
      .then((res) => {
        setShowModal(false);
        showGlobalModal(() => <AlertModal title={t('App.info')} message={res.message} />);
      })
      .catch((e: AxiosError) => {
        setShowModal(false);
        const data: any = e.response?.data;
        showGlobalModal(() => <AlertModal title={t('App.info')} message={data?.message} />);
      });
    resetTargetSchema();
  };

  return (
    <Modal aria-labelledby="contained-modal-title-vcenter" centered show={showModal}>
      <Modal.Body>
        <Row className="mb-3">
          <Col sm={9}>
            <div className="p-2 h5" style={{ backgroundColor: '#E1D5E7' }}>
              <strong>{t('DeliveryFeature.Text.distribution_settings')}</strong>
            </div>
          </Col>
          <Col></Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.target_schema')}
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ minWidth: '180px' }}>
              <Form.Select onChange={handleSelectedTargetSchema} value={selectedTargetSchema}>
                {targetSchemaList.map((option) => (
                  <option key={option.target_schema} value={option.target_schema}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </Col>
        </Row>
        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.test_date')}
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ minWidth: '180px' }}>
              <DatePicker
                locale="ja"
                selected={selectedDate}
                onChange={handleDateChange}
                customInput={<CustomInputDatePicker />}
                dateFormat="yyyy-MM-dd"
              />
            </div>
          </Col>
        </Row>

        <Row className="mb-3">
          <Form.Label column sm="3">
            {t('DeliveryFeature.Text.distribution_unit')}:
          </Form.Label>
          <Col sm="9">
            <div className="d-flex" style={{ minWidth: '180px' }}>
              <Form.Select>
                {UNIT_TEST_REFLECTION.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.name}
                  </option>
                ))}
              </Form.Select>
            </div>

            {showSelectDepartmentModal && (
              <div className="my-3">
                <SelectSingleRestaurantModal
                  defaultDataDate={dataDate || new Date()}
                  showDataDatePicker={false}
                  onClose={() => setShowSelectDepartmentModal(false)}
                  onReflect={handleReturnSelectedRestaurant}
                />
              </div>
            )}
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="d-grid gap-2 d-md-flex justify-content-md-end">
              <button className="btn btn-primary me-md-2" type="button" onClick={handleCreateDeliverySettings}>
                {t('DeliveryFeature.Component.setting')}
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => {
                  setShowModal(false);
                  resetTargetSchema();
                }}
              >
                {t('DeliveryFeature.Component.close')}
              </button>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
});

export default TestReflectionModal;
